import { useState } from "react";
import Tag from "@/components/Tag";
import type { Action } from "@/components/MenuButton";
import Table, {
  TableControls,
  toTableControlActions,
  buildActionColumn,
  buildTwoLineColumn,
  buildExpandableCountColumn,
  type TableAction
} from "@/components/Table";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import EmptyState from "@/components/EmptyState";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useFlags } from "@/hooks/useFeatureFlags";
import type { EducationCertification, PgmProgramRequirement } from "@/types";
import { canEditEducationRequirementType } from "@/features/person/utils/education-requirement-reason-codes";
import {
  INCOMPLETE_LIFELONG_LEARNING_LEVEL_MESSAGE,
  personHasIncompleteLifelongLearningLevel
} from "@/features/person/utils/education-program-level-options";
import { showErrorSnackbar } from "@/lib/snackbarUtils";
import { formatCertificationProgramData, type LifelongLearningProgressRow } from "./utils";
import type { ProgramDetailRow } from "../PGMProgramTable/utils";
import DeleteProgramRequirementDialog, {
  type ProgramRequirementToDelete
} from "../PGMProgramTable/DeleteProgramRequirementDialog";
import DeleteEducationProgramLevelDialog, {
  type EducationProgramLevelToDelete
} from "../PGMProgramTable/DeleteEducationProgramLevelDialog";
import EditEducationRequirementModal from "../PGMProgramTable/EditEducationRequirementModal";
import AddEducationProgramLevelModal from "../PGMProgramTable/AddEducationProgramLevelModal";
import EditEducationProgramLevelModal, {
  type EducationProgramLevelToEdit
} from "../PGMProgramTable/EditEducationProgramLevelModal";
import { lifelongLearningRefetchQueries } from "../PGMProgramTable/mutations";

export interface LifelongLearningTableProps {
  certificationHistory?: EducationCertification[];
  loading?: boolean;
  testId?: string;
  personId?: string;
  canManageEducationRequirements?: boolean;
  canDeleteEducationProgramLevels?: boolean;
}

export default function LifelongLearningTable({
  certificationHistory = [],
  loading = false,
  testId = "learning-history-table",
  personId = "",
  canManageEducationRequirements = false,
  canDeleteEducationProgramLevels = false
}: LifelongLearningTableProps) {
  const [requirementToDelete, setRequirementToDelete] = useState<ProgramRequirementToDelete | null>(null);
  const [levelToDelete, setLevelToDelete] = useState<EducationProgramLevelToDelete | null>(null);
  const [selectedRequirement, setSelectedRequirement] = useState<{
    requirement: PgmProgramRequirement;
    levelStartDate: string;
  } | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<EducationProgramLevelToEdit | null>(null);
  const [showAddLevelModal, setShowAddLevelModal] = useState(false);
  const { Menu, setMenu } = useTableRowMenu();
  const flags = useFlags();
  const refetchQueries = personId ? lifelongLearningRefetchQueries(personId) : [];

  const showManageLifelongLearningWrites =
    canManageEducationRequirements && !!flags["backoffice-manage-lifelong-learning"];
  const showEditEducationRequirementActions = showManageLifelongLearningWrites;
  const showDeleteEducationProgramLevelActions =
    showManageLifelongLearningWrites &&
    canDeleteEducationProgramLevels &&
    !!flags["backoffice-education-delete-program-level"];
  const showWriteActions = showEditEducationRequirementActions || showDeleteEducationProgramLevelActions;

  const hasIncompleteLevel = personHasIncompleteLifelongLearningLevel(certificationHistory);
  const tableData = formatCertificationProgramData(certificationHistory);

  const openAddLevel = () => {
    if (hasIncompleteLevel) {
      showErrorSnackbar(INCOMPLETE_LIFELONG_LEARNING_LEVEL_MESSAGE);
      return;
    }

    setShowAddLevelModal(true);
  };

  const headingActions: TableAction[] =
    showEditEducationRequirementActions && !loading
      ? [
          {
            label: "Add Level",
            icon: "add",
            variant: "outlined",
            testId: "add-lifelong-learning-level-action",
            onClick: openAddLevel,
            ariaLabel: hasIncompleteLevel ? `Add Level. ${INCOMPLETE_LIFELONG_LEARNING_LEVEL_MESSAGE}` : "Add Level"
          }
        ]
      : [];
  const tableControlActions = toTableControlActions(headingActions);

  const showControls = tableControlActions.length > 0;

  const levelColumn = buildTwoLineColumn<LifelongLearningProgressRow>({
    accessorKey: "level",
    header: "Level",
    getPrimaryText: (row) => row.programDescription,
    getSecondaryText: (row) => (row.code !== row.programName ? row.code : ""),
    size: 300,
    isFirstColumn: true
  });

  const columns = [
    {
      ...levelColumn,
      meta: {
        ...levelColumn.meta,
        className: getStickyColumnClasses(tableData.length > 0)
      }
    },
    {
      accessorKey: "version",
      header: "Version",
      enableSorting: false
    },
    {
      accessorKey: "careerPath",
      header: "Career Path",
      enableSorting: false
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      enableSorting: false
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      enableSorting: false
    },
    {
      accessorKey: "expiresDate",
      header: "Expiration",
      enableSorting: false,
      cell: ({ row }: { row: { original: LifelongLearningProgressRow } }) => (
        <span className={row.original.isExpired ? "text-sys-brand-error" : ""}>{row.original.expiresDate || "--"}</span>
      )
    },
    buildExpandableCountColumn<LifelongLearningProgressRow>({
      accessorKey: "requirements",
      header: "Requirements",
      getCount: (row) => row.completedDetailsCount,
      getTotal: (row) => row.detailsCount
    }),
    {
      accessorKey: "status",
      header: "Level Status",
      cell: ({ row }: { row: { original: LifelongLearningProgressRow } }) => (
        <Tag tagType={row.original.status === "Complete" ? "success" : "warn"} size="small">
          {row.original.status}
        </Tag>
      ),
      meta: {
        headerClassName: "hide-header-in-column"
      },
      enableSorting: false
    },
    ...(showWriteActions
      ? [
          buildActionColumn<LifelongLearningProgressRow>({
            getActions: (row): Action[] => {
              if (!row.levelCode?.trim()) return [];

              return [
                ...(showEditEducationRequirementActions
                  ? ([
                      {
                        text: "Edit Level",
                        section: "Manage",
                        testId: "edit-lifelong-learning-level-action",
                        onClick: () =>
                          setSelectedLevel({
                            completeCode: row.completeCode,
                            endDateIso: row.endDateIso,
                            levelCode: row.levelCode,
                            levelLabel: row.level,
                            remarks: row.remarks,
                            startDateIso: row.startDateIso
                          })
                      }
                    ] satisfies Action[])
                  : []),
                ...(showDeleteEducationProgramLevelActions
                  ? ([
                      {
                        text: "Delete Level",
                        iconName: "delete",
                        destructive: true,
                        testId: "delete-lifelong-learning-level-action",
                        onClick: () =>
                          setLevelToDelete({
                            levelLabel: row.level,
                            levelCode: row.levelCode,
                            startDateIso: row.startDateIso
                          })
                      }
                    ] satisfies Action[])
                  : [])
              ];
            },
            actionId,
            setMenu,
            getRowId: (row) => row.id
          })
        ]
      : [])
  ];

  const detailsColumns = [
    buildTwoLineColumn<ProgramDetailRow>({
      accessorKey: "description",
      header: "Description",
      getPrimaryText: (row) => row.description,
      getSecondaryText: (row) => row.secondaryText,
      size: 300,
      isFirstColumn: true
    }),
    {
      accessorKey: "requireTypeLabel",
      header: "Type",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => (
        <span>{detailRow.original.requireTypeLabel || "--"}</span>
      ),
      enableSorting: false,
      size: 120
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => (
        <span>{detailRow.original.date || "--"}</span>
      ),
      enableSorting: false,
      size: 150
    },
    {
      accessorKey: "scoreText",
      header: "Score",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) =>
        detailRow.original.scoreText ? (
          <span>{detailRow.original.scoreText}</span>
        ) : (
          <span className="opacity-20">--</span>
        ),
      enableSorting: false,
      size: 100
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => (
        <Tag tagType={detailRow.original.statusType} size="small">
          {detailRow.original.statusLabel}
        </Tag>
      ),
      meta: {
        headerClassName: "hide-header-in-column"
      },
      enableSorting: false,
      size: 120
    },
    ...(showEditEducationRequirementActions
      ? [
          buildActionColumn<ProgramDetailRow>({
            getActions: (row) => {
              if (row.type !== "Requirement" || !row.requireId) return [];
              if (row.requirement?.archived) return [];

              const requireId = row.requireId;
              const requirement = row.requirement;
              const levelStartDate = row.levelStartDate || "";
              const canEditThisRequirement =
                showEditEducationRequirementActions &&
                !!requirement &&
                canEditEducationRequirementType(requirement.requirementDefinition?.requireType);

              const actions: Action[] = [];

              if (canEditThisRequirement) {
                actions.push({
                  text: "Edit Requirement",
                  section: "Manage",
                  testId: "edit-lifelong-learning-requirement-action",
                  onClick: () => {
                    setSelectedRequirement({ requirement, levelStartDate });
                  }
                });
              }

              actions.push({
                text: "Delete Requirement",
                iconName: "delete",
                destructive: true,
                onClick: () =>
                  setRequirementToDelete({
                    description: row.description,
                    requireId,
                    levelCode: row.levelCode
                  })
              });

              return actions;
            },
            actionId,
            setMenu,
            getRowId: (row) => row.id
          })
        ]
      : [])
  ];

  return (
    <>
      <Menu />
      <Table
        data={tableData}
        columns={columns}
        loading={loading}
        data-testid={testId}
        renderControls={
          showControls
            ? (_table, { onSortClick } = {}) => (
                <TableControls onSortClick={onSortClick} actions={tableControlActions} />
              )
            : undefined
        }
        getRowCanExpand={(row) => row.original.detailsCount > 0}
        renderExpandedRow={({ row }) => (
          <TableDetailsPanel
            data={row.original.details}
            columns={detailsColumns}
            title={`Requirements (${row.original.completedDetailsCount}/${row.original.detailsCount})`}
          />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Programs"
            caption="This person has not started any lifelong learning programs"
            iconName="workspace_premium"
          />
        )}
      />
      {personId ? (
        <>
          <DeleteProgramRequirementDialog
            open={!!requirementToDelete}
            requirement={requirementToDelete}
            pgaId={personId}
            onClose={() => setRequirementToDelete(null)}
            catalog="LIFELONG_LEARNING"
            refetchQueries={refetchQueries}
          />
          <DeleteEducationProgramLevelDialog
            open={!!levelToDelete}
            level={levelToDelete}
            pgaId={personId}
            onClose={() => setLevelToDelete(null)}
            catalog="LIFELONG_LEARNING"
            refetchQueries={refetchQueries}
          />
          {selectedRequirement ? (
            <EditEducationRequirementModal
              open
              onClose={() => setSelectedRequirement(null)}
              pgaId={personId}
              requirement={selectedRequirement.requirement}
              levelStartDate={selectedRequirement.levelStartDate}
              catalog="LIFELONG_LEARNING"
              refetchQueries={refetchQueries}
            />
          ) : null}
          {showAddLevelModal ? (
            <AddEducationProgramLevelModal
              open
              onClose={() => setShowAddLevelModal(false)}
              pgaId={personId}
              catalog="LIFELONG_LEARNING"
              refetchQueries={refetchQueries}
            />
          ) : null}
          {selectedLevel ? (
            <EditEducationProgramLevelModal
              open
              onClose={() => setSelectedLevel(null)}
              pgaId={personId}
              level={selectedLevel}
              catalog="LIFELONG_LEARNING"
              refetchQueries={refetchQueries}
            />
          ) : null}
        </>
      ) : null}
    </>
  );
}
